import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/useAuth';
import { getSocket } from '@/hooks/useRealtimeSync';
import { markProjectUpdatedByMutation } from '@/lib/projectRealtimeDedup';
import { ApiError } from '@/api/client';
import { acceptServerList, beginListChange, holdServerList, serverListForQuery } from '@/lib/serverListSync';
import {
  isSupportedProjectMetricsFile,
  tryComputeAuthorSheetsAndPagesFromFile,
} from '@/lib/projectFileMetrics/authorSheetsCalculator';
import {
  PROJECT_FILES_MAX_BYTES,
  PROJECT_FILES_MAX_FILENAME_LENGTH,
  PROJECT_FILES_MAX_SIZE_LABEL,
} from './projectFilesPopoverConstants';
import { sortProjectFilesNewestFirst } from './projectFilesPopoverUtils';
import type { EntityFilesPopoverRow, EntityFilesPopoverServerProps, FilesRevisionList } from './entityFilesPopover.types';

export type EntityFilesUploadUi =
  | { step: 'metrics'; fileName: string }
  | { step: 'server'; fileName: string; pct: number }
  | { step: 'storage'; fileName: string; pct: number };

/**
 * Загрузка одного файла — строкой в списке (новый дизайн): ждёт очереди, передаётся (pct — весь путь до хранилища, 0–100;
 * 100 — ждём ответа сервера). Ответ пришёл — строка становится файлом с тем же ключом (rowKeys).
 */
export interface EntityFileUpload {
  key: string;
  fileName: string;
  phase: 'queued' | 'metrics' | 'transfer';
  pct: number;
  /** Сервер ответил: id созданного файла. Строка загрузки видна, пока этого файла нет в списке, — смена в одной отрисовке */
  fileId: string | null;
}

let uploadSeq = 0;

export const ENTITY_FILES_UPLOAD_STEP_TITLES: Record<EntityFilesUploadUi['step'], string> = {
  metrics: 'Подсчёт авторских листов',
  server: 'Загрузка файла на сервер',
  storage: 'Загрузка файла в хранилище',
};

export type EntityFilesManagerOptions<T extends EntityFilesPopoverRow> = Pick<
  EntityFilesPopoverServerProps<T>,
  'filesQuery' | 'filesApi' | 'realtimeDedupProjectId' | 'onMutationsSettled'
> & {
  /** Список виден пользователю: включает загрузку и тост об ошибке загрузки. */
  active: boolean;
};

/**
 * Файлы сущности (проект или чтец): список, загрузка с прогрессом (клиент → сервер → хранилище),
 * удаление и правка с оптимистичным обновлением кэша. UI (попап, блок) — снаружи.
 */
/** Ответ списка: с ревизией (новый дизайн) или просто массив (старое окно файлов, файлы чтеца) */
function asRevisionList<T>(r: T[] | FilesRevisionList<T>): FilesRevisionList<T> {
  return Array.isArray(r) ? { files: r, revision: null } : r;
}

/** Ревизия записи из ответа правки или удаления; старый вызов без ревизии — null */
function revisionOf(r: unknown): number | null {
  return r !== null && typeof r === 'object' && 'revision' in r && typeof r.revision === 'number' ? r.revision : null;
}

export function useEntityFilesManager<T extends EntityFilesPopoverRow>({
  active,
  filesQuery,
  filesApi,
  realtimeDedupProjectId,
  onMutationsSettled,
}: EntityFilesManagerOptions<T>) {
  const queryClient = useQueryClient();
  const { showError: showToastError, showSuccess: showToastSuccess } = useToast();
  const { user } = useAuth();
  const queryKey = filesQuery.queryKey;

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [uploadUi, setUploadUi] = useState<EntityFilesUploadUi | null>(null);
  const isUploading = uploadUi !== null;
  const [uploads, setUploads] = useState<readonly EntityFileUpload[]>([]);
  /** Файл, который был строкой загрузки, — ключ той строки: строка остаётся на месте и только меняет содержимое */
  const [rowKeys, setRowKeys] = useState<ReadonlyMap<string, string>>(() => new Map());
  const patchUpload = useCallback(
    (key: string, patch: Partial<EntityFileUpload>) =>
      setUploads((list) => list.map((u) => (u.key === key ? { ...u, ...patch, pct: Math.max(u.pct, patch.pct ?? u.pct) } : u))),
    []
  );

  const {
    data: files,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey,
    // Снимок сервера (с ревизией — устаревший отбрасывается) и поверх него свои незавершённые правки
    queryFn: async () => {
      const r = asRevisionList(await filesQuery.queryFn());
      return serverListForQuery(queryClient, queryKey, r.files, r.revision);
    },
    staleTime: 60_000,
    enabled: active,
  });

  const fetchErrorToastedRef = useRef(false);
  useEffect(() => {
    if (!active || !isError) {
      fetchErrorToastedRef.current = false;
      return;
    }
    if (!error || fetchErrorToastedRef.current) return;
    fetchErrorToastedRef.current = true;
    showToastError((error as Error).message || 'Не удалось загрузить файлы');
  }, [active, isError, error, showToastError]);

  useEffect(() => {
    const socket = getSocket();
    const handler = (data: { pct: number; phase: string }) => {
      if (data.phase !== 'storage') return;
      const storagePct = Math.min(100, Math.max(0, Math.round(Number(data.pct))));
      setUploads((list) => list.map((u) => (u.phase === 'transfer' ? { ...u, pct: Math.max(u.pct, 50 + storagePct / 2) } : u)));
      setUploadUi((prev) => {
        if (!prev || prev.step === 'metrics') return prev;
        const nextPct = storagePct;
        if (prev.step === 'server') {
          return { step: 'storage', fileName: prev.fileName, pct: nextPct };
        }
        if (prev.step === 'storage') {
          return { ...prev, pct: Math.max(prev.pct, nextPct) };
        }
        return prev;
      });
    };
    socket.on('file:upload:progress', handler);
    return () => {
      socket.off('file:upload:progress', handler);
    };
  }, []);

  // Своя правка — сразу на экране и поверх любого следующего снимка сервера, пока он её не подтвердит ревизией;
  // ошибка — правка снимается, на экране снимок сервера с чужими изменениями (lib/serverListSync)
  const deleteMutation = useMutation({
    mutationFn: (fileId: string) => filesApi.delete(fileId),
    onMutate: async (fileId) => {
      if (realtimeDedupProjectId) markProjectUpdatedByMutation(realtimeDedupProjectId);
      await queryClient.cancelQueries({ queryKey });
      return { change: beginListChange<T>(queryClient, queryKey, (list) => list.filter((f) => f.file_id !== fileId)) };
    },
    onSuccess: (result, _fileId, context) => context?.change.confirm(revisionOf(result)),
    onError: (err, _fileId, context) => {
      // Файл уже удалил кто-то другой — цель достигнута: не ошибка и файл не возвращается
      if (err instanceof ApiError && err.status === 404) {
        context?.change.confirm(null);
        return;
      }
      context?.change.cancel();
      void queryClient.invalidateQueries({ queryKey });
      showToastError((err as Error).message || 'Ошибка удаления файла');
    },
    onSettled: () => {
      setDeletingId(null);
      onMutationsSettled();
    },
  });

  const updateFileMutation = useMutation({
    mutationFn: ({
      fileId,
      patch,
    }: {
      fileId: string;
      patch: { file_name?: string; file_type?: string | null };
    }) => filesApi.update(fileId, patch),
    onMutate: async (vars) => {
      if (realtimeDedupProjectId) markProjectUpdatedByMutation(realtimeDedupProjectId);
      await queryClient.cancelQueries({ queryKey });
      const change = beginListChange<T>(queryClient, queryKey, (list) =>
        list.map((f) => (f.file_id === vars.fileId ? { ...f, ...vars.patch } : f))
      );
      return { change };
    },
    onError: (err, _vars, context) => {
      context?.change.cancel();
      void queryClient.invalidateQueries({ queryKey });
      showToastError((err as Error).message || 'Не удалось обновить файл');
    },
    onSuccess: (result, variables, context) => {
      context?.change.confirm(revisionOf(result));
      if (variables.patch.file_name !== undefined) {
        showToastSuccess('Название файла обновлено');
      }
    },
    onSettled: () => {
      onMutationsSettled();
    },
  });

  /** Удаление, которое пользователь уже подтвердил в своём интерфейсе (меню файла нового дизайна) */
  const deleteFileConfirmed = useCallback(
    (fileId: string) => {
      if (deletingId) return;
      setDeletingId(fileId);
      deleteMutation.mutate(fileId);
    },
    [deletingId, deleteMutation]
  );

  /** Удаление с вопросом браузера — для старого окна файлов */
  const deleteFile = useCallback(
    (fileId: string) => {
      if (deletingId) return;
      if (!window.confirm('Удалить файл? Это действие нельзя отменить.')) return;
      deleteFileConfirmed(fileId);
    },
    [deletingId, deleteFileConfirmed]
  );

  const patchFile = useCallback(
    (fileId: string, patch: { file_type?: string | null }) => {
      updateFileMutation.mutate({ fileId, patch });
    },
    [updateFileMutation]
  );

  const renameFile = useCallback(
    async (fileId: string, fileName: string) => {
      await updateFileMutation.mutateAsync({ fileId, patch: { file_name: fileName } });
    },
    [updateFileMutation]
  );

  const refreshFiles = useCallback(async () => {
    const r = asRevisionList(await filesApi.list());
    acceptServerList(queryClient, queryKey, r.files, r.revision);
    onMutationsSettled();
  }, [filesApi, queryClient, queryKey, onMutationsSettled]);

  const uploadFiles = useCallback(
    async (selectedFiles: File[]) => {
      if (selectedFiles.length === 0) return;

      for (const file of selectedFiles) {
        if (file.size > PROJECT_FILES_MAX_BYTES) {
          showToastError(
            `Файл «${file.name}» слишком большой. Максимальный размер: ${PROJECT_FILES_MAX_SIZE_LABEL}.`
          );
          return;
        }
        if (file.name.length > PROJECT_FILES_MAX_FILENAME_LENGTH) {
          showToastError(
            `Название файла «${file.name}» слишком длинное. Максимум ${PROJECT_FILES_MAX_FILENAME_LENGTH} символов.`
          );
          return;
        }
      }

      setUploadUi(null);
      // Все выбранные файлы — сразу строками сверху списка; грузятся по одному. Сверху — последний: список свежими вверх,
      // поэтому загружаемый сейчас стоит прямо над файлами и становится верхним файлом на том же месте
      const queued: EntityFileUpload[] = selectedFiles.map((file) => ({ key: `upload-${++uploadSeq}`, fileName: file.name, phase: 'queued', pct: 0, fileId: null }));
      setUploads((list) => [...[...queued].reverse(), ...list]);
      let release: (() => void) | null = null;
      try {
        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i]!;
          const row = queued[i]!;
          const displayName =
            selectedFiles.length > 1 ? `${file.name} (${i + 1} из ${selectedFiles.length})` : file.name;

          let clientMetrics: { authorSheets: number; pages: number } | null = null;
          if (isSupportedProjectMetricsFile(file)) {
            patchUpload(row.key, { phase: 'metrics' });
            setUploadUi({ step: 'metrics', fileName: displayName });
            const metricsTry = await tryComputeAuthorSheetsAndPagesFromFile(file);
            if (!metricsTry.ok) {
              showToastError(
                `Не удалось вычислить АЛ/страницы для «${file.name}»: ${metricsTry.errorMessage}`
              );
            } else {
              clientMetrics = metricsTry.metrics;
            }
          }

          patchUpload(row.key, { phase: 'transfer' });
          setUploadUi({ step: 'server', fileName: displayName, pct: 0 });
          const created = await filesApi.upload(file, user?.id ?? null, (xhrPct) => {
            const pct = Math.min(100, Math.max(0, Math.round(xhrPct)));
            patchUpload(row.key, { pct: pct / 2 });
            // Файл передан, сервер кладёт его в хранилище и в список: снимок с ним может прийти раньше ответа
            if (pct >= 100 && !release) release = holdServerList(queryClient, queryKey);
            setUploadUi((prev) =>
              prev && prev.step === 'server' && prev.fileName === displayName ? { ...prev, pct } : prev
            );
          }, undefined, clientMetrics);
          // Ответ — новый файл: сразу в список (своей правкой до следующего снимка) на месте строки загрузки.
          // Повтор запроса сервер отвечает без файла — тогда файл придёт с перечитыванием в конце
          if (created?.file_id) {
            beginListChange<T>(queryClient, queryKey, (list) =>
              list.some((f) => f.file_id === created.file_id) ? list : [created, ...list]
            ).confirm(null);
            setRowKeys((keys) => new Map(keys).set(created.file_id, row.key));
            patchUpload(row.key, { fileId: created.file_id });
          } else {
            setUploads((list) => list.filter((u) => u.key !== row.key));
          }
          (release as (() => void) | null)?.();
          release = null;

          setUploadUi((prev) => {
            if (!prev || prev.fileName !== displayName) return prev;
            if (prev.step === 'server') {
              return { step: 'storage', fileName: displayName, pct: 100 };
            }
            if (prev.step === 'storage') {
              return { ...prev, pct: Math.max(prev.pct, 100) };
            }
            return prev;
          });
        }
        setUploadUi(null);
        await refreshFiles();
        showToastSuccess(selectedFiles.length > 1 ? `Загружено файлов: ${selectedFiles.length}` : 'Файл загружен');
      } catch (err) {
        const message = (err as Error).message;
        if (message === 'Загрузка отменена') return;
        if (
          message.includes('слишком большой') ||
          message.includes('МБ') ||
          message.includes('File too large') ||
          message.includes('LIMIT_FILE_SIZE')
        ) {
          showToastError(`Файл слишком большой. Максимальный размер: ${PROJECT_FILES_MAX_SIZE_LABEL}.`);
        } else if (message.includes('слишком длинное') || message.includes('символов')) {
          showToastError(message);
        } else {
          showToastError(message || 'Ошибка загрузки файла');
        }
      } finally {
        (release as (() => void) | null)?.();
        // Ошибка — строки этой загрузки (и очередь за ней) уходят; загруженные уходят сами, когда их файл в списке
        const mine = new Set(queued.map((u) => u.key));
        setUploads((list) => list.filter((u) => !mine.has(u.key) || u.fileId !== null));
        setUploadUi(null);
      }
    },
    [filesApi, user?.id, refreshFiles, showToastError, showToastSuccess, patchUpload, queryClient, queryKey]
  );

  const filesSorted = useMemo(() => sortProjectFilesNewestFirst(files ?? []), [files]);
  // Файл загрузки уже в списке — строка загрузки больше не нужна (на экране её и так нет: FilesList)
  useEffect(() => {
    if (!files || !uploads.some((u) => u.fileId !== null)) return;
    const ids = new Set(files.map((f) => f.file_id));
    setUploads((list) => (list.some((u) => u.fileId !== null && ids.has(u.fileId)) ? list.filter((u) => u.fileId === null || !ids.has(u.fileId)) : list));
  }, [files, uploads]);

  return {
    /** Файлы, свежие сверху */
    files: filesSorted,
    isPending,
    isError,
    error: error as Error | null,
    uploadUi,
    isUploading,
    /** Загрузки строками списка (новый дизайн), свежие сверху */
    uploads,
    /** Ключ строки файла: был строкой загрузки — её ключ, иначе id файла */
    rowKeys,
    uploadFiles,
    deleteFile,
    deleteFileConfirmed,
    deletingId,
    isDeleteBusy: deleteMutation.isPending,
    patchFile,
    renameFile,
    /** id файла, который сейчас правится (переименование/тип) */
    updatingFileId: updateFileMutation.isPending ? (updateFileMutation.variables?.fileId ?? null) : null,
    isUpdateBusy: updateFileMutation.isPending,
    /** Перечитать список (снимок сервера; свои незавершённые правки остаются поверх) */
    refreshFiles,
  };
}
